import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/accounting/opening` — what the company walked into Hub with.
 *
 * The figures come from whoever kept the books before, so the page is
 * built around pasting them in rather than typing them: an accountant
 * exports a trial balance and drops it here. Posting happens once, and
 * the page says so plainly — a second posting would double everything
 * the year started with.
 */

const searchSchema = z.object({ year: z.number().optional() });

type Row = {
  id: string;
  accountCode: string;
  debit: string;
  credit: string;
  note: string | null;
};

type Response = {
  year: number;
  rows: Row[];
  totals: { debit: string; credit: string; difference: string; balanced: boolean };
  posted: { id: string; number: number; bookedOn: string } | null;
};

export const Route = createFileRoute("/_app/accounting/opening")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ year: search.year }),
  loader: async ({ deps }) => {
    try {
      return await api.get<Response>(
        `/api/accounting/opening-balances${deps.year ? `?year=${deps.year}` : ""}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: OpeningPage,
});

function money(amount: string): string {
  return formatNumber(Number(amount), undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function OpeningPage() {
  const { t } = useTranslation();
  const data = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const [text, setText] = useState("");
  const [details, setDetails] = useState<string[]>([]);

  const save = useApiMutation(
    () => api.put("/api/accounting/opening-balances", { year: data.year, text }),
    {
      onSuccess: () => {
        setText("");
        setDetails([]);
        router.invalidate();
      },
      errorMessage: (err) => {
        // The server names the lines it couldn't read; showing only
        // "didn't work" would leave the accountant hunting for which one.
        const detail = err instanceof ApiError ? (err.detail as { details?: string[] }) : null;
        setDetails(detail?.details ?? []);
        return t("accOpening.readFailed");
      },
    },
  );

  const post = useApiMutation(
    () => api.post("/api/accounting/opening-balances/post", { year: data.year }),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accOpening.postFailed") },
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">{t("accOpening.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("accOpening.subtitle")}</p>
        </div>
        <label className="text-sm">
          <span className="text-muted-foreground text-xs">{t("accOpening.year")}</span>
          <input
            type="number"
            defaultValue={data.year}
            className="mt-1 block h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) =>
              navigate({ to: "/accounting/opening", search: { year: Number(e.target.value) } })
            }
          />
        </label>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">
              {data.posted
                ? t("accOpening.posted")
                : data.rows.length > 0
                  ? t("accOpening.readyToPost")
                  : t("accOpening.nothingYet")}
            </CardTitle>
            <CardDescription>
              {t("accOpening.totals", {
                debit: money(data.totals.debit),
                credit: money(data.totals.credit),
              })}
              {!data.totals.balanced &&
                t("accOpening.totalsDifference", { difference: money(data.totals.difference) })}
            </CardDescription>
          </div>
          {data.posted ? (
            <Badge variant="success">
              {t("accOpening.journalEntryNo", { number: data.posted.number })}
            </Badge>
          ) : (
            <Button
              disabled={post.pending || data.rows.length === 0 || !data.totals.balanced}
              onClick={() => post.mutate()}
            >
              {post.pending ? t("accOpening.posting") : t("accOpening.postToJournal")}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!data.totals.balanced && data.rows.length > 0 && (
            <p className="text-destructive text-sm">
              {t("accOpening.unbalanced", { difference: money(data.totals.difference) })}
            </p>
          )}
          {post.error && <p className="text-destructive text-sm">{post.error}</p>}
          {data.posted && (
            <p className="text-muted-foreground text-sm">{t("accOpening.postedOnce")}</p>
          )}

          {data.rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accOpening.account")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accOpening.debit")}</th>
                  <th className="py-1 text-right font-medium">{t("accOpening.credit")}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="py-1 pr-4 tabular-nums">{row.accountCode}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {Number(row.debit) > 0 ? money(row.debit) : "—"}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {Number(row.credit) > 0 ? money(row.credit) : "—"}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold">
                  <td className="py-1 pr-4">{t("accOpening.total")}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{money(data.totals.debit)}</td>
                  <td className="py-1 text-right tabular-nums">{money(data.totals.credit)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {!data.posted && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("accOpening.pasteTitle")}</CardTitle>
            <CardDescription>
              <Trans i18nKey="accOpening.pasteHint" components={{ code: <code /> }} />
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              value={text}
              rows={8}
              placeholder={t("accOpening.pastePlaceholder")}
              className="w-full rounded-md border border-input bg-background p-2 font-mono text-sm"
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                disabled={save.pending || text.trim().length === 0}
                onClick={() => save.mutate()}
              >
                {save.pending ? t("accOpening.reading") : t("accOpening.readAndSave")}
              </Button>
              {save.error && <p className="text-destructive text-sm">{save.error}</p>}
            </div>
            {details.length > 0 && (
              <ul className="space-y-1 text-destructive text-xs">
                {details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
